import { useId, type ReactNode } from 'react';

import { cn } from '../../lib/cn';

/**
 * The label / hint / error plumbing shared by Input, Textarea and Select.
 *
 * docs/02-DESIGN-SYSTEM.md §7.6 — "every field has a `<label>` (not just a
 * placeholder); errors are announced via `aria-live="polite"` and linked with
 * `aria-describedby`". Getting that wiring right once, here, is why the three
 * controls are thin.
 *
 * `label` is required rather than optional. A field whose only label is a
 * placeholder disappears from a screen reader the moment the user types, and
 * the Documents search box in the mockups is exactly that shape — so it takes
 * `hideLabel` and keeps a real label in the accessibility tree.
 */
export interface FieldOwnProps {
  /** Required. Comes from the i18n bundle, never hardcoded (CLAUDE.md rule 2). */
  label: string;
  /** Visually hides the label without removing it from the accessibility tree. */
  hideLabel?: boolean;
  /** Helper text below the control, e.g. an accepted file format. */
  hint?: string;
  /** Validation message. Its presence marks the control invalid. */
  error?: string;
  required?: boolean;
  id?: string;
  className?: string;
}

export interface FieldRenderProps {
  id: string;
  'aria-describedby': string | undefined;
  'aria-invalid': true | undefined;
  'aria-required': true | undefined;
}

interface FieldProps extends FieldOwnProps {
  children: (props: FieldRenderProps) => ReactNode;
}

/** Shared control styling: §4.1 grey fill, no rest border, navy focus ring. */
export const CONTROL_CLASSES =
  'w-full rounded-md bg-surface-alt px-4 text-body text-primary ' +
  'placeholder:text-muted ' +
  'border border-transparent ' +
  'transition-colors duration-fast ' +
  'focus:border-navy-600 focus:outline-none focus:ring-2 focus:ring-navy-600 ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

export const CONTROL_INVALID_CLASSES = 'border-danger focus:border-danger focus:ring-danger';

export function Field({
  label,
  hideLabel = false,
  hint,
  error,
  required = false,
  id,
  className,
  children,
}: FieldProps) {
  const generated = useId();
  const fieldId = id ?? generated;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;

  // Both are linked when both exist, so the hint is not lost the moment the
  // field goes invalid.
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <label
        htmlFor={fieldId}
        className={cn(
          'text-body-sm font-semibold tracking-wide text-primary',
          hideLabel && 'sr-only',
        )}
      >
        {label}
        {required ? (
          // The asterisk is decoration; `aria-required` on the control is what
          // actually communicates this (§7.4 — never colour or glyph alone).
          <span aria-hidden="true" className="ms-1 text-danger">
            {'*'}
          </span>
        ) : null}
      </label>

      {children({
        id: fieldId,
        'aria-describedby': describedBy === '' ? undefined : describedBy,
        'aria-invalid': error ? true : undefined,
        'aria-required': required ? true : undefined,
      })}

      {hint ? (
        <p id={hintId} className="text-body-sm text-secondary">
          {hint}
        </p>
      ) : null}

      {/*
        Always rendered so the live region exists before it has anything to
        say. A region inserted at the same moment as its content is frequently
        missed by screen readers.
      */}
      <p
        id={errorId}
        role="alert"
        aria-live="polite"
        className="text-body-sm text-danger empty:hidden"
      >
        {error}
      </p>
    </div>
  );
}
